import dayjs from 'dayjs'
import type { MuhurtaSlot } from '@/lib/api'
import { cn } from '@/lib/utils'
import { CurrentTimeIndicator } from './CurrentTimeIndicator'

const QUALITY_TRACK: Record<string, string> = {
  excellent: 'bg-emerald-500/15 border-l-2 border-emerald-500/50 hover:bg-emerald-500/20',
  good:      'bg-amber-500/10  border-l-2 border-amber-500/40  hover:bg-amber-500/15',
  neutral:   'bg-cosmic-void/50 ring-1     ring-white/5          hover:bg-cosmic-nebula/50',
  avoid:     'opacity-40       bg-red-500/5 border-l-2 border-red-500/20',
}

const QUALITY_DOT: Record<string, string> = {
  excellent: 'bg-emerald-400',
  good:      'bg-amber-400',
  neutral:   'bg-star-muted',
  avoid:     'bg-red-400/60',
}

const QUALITY_SCORE_BAR: Record<string, string> = {
  excellent: 'bg-emerald-500',
  good:      'bg-amber-500',
  neutral:   'bg-star-dim',
  avoid:     'bg-red-500/60',
}

interface Props {
  slots: MuhurtaSlot[]
}

/**
 * Interactive horizontal Muhurta timeline.
 * Blocks are positioned absolutely by real clock time.
 * CurrentTimeIndicator floats above as a glassmorphic pill.
 */
export function MuhurtaTimeline({ slots }: Props) {
  if (!slots.length) {
    return <p className="text-star-dim text-xs font-mono">No muhurta windows today</p>
  }

  const timestamps = slots.flatMap(s => [
    dayjs(s.startTime).valueOf(),
    dayjs(s.endTime).valueOf(),
  ])
  const minMs = Math.min(...timestamps)
  const maxMs = Math.max(...timestamps)
  const span  = maxMs - minMs || 1

  return (
    <div className="space-y-4">
      {/* ── Horizontal bar ── */}
      <div className="relative mt-7 h-12 rounded-xl bg-cosmic-void/40 ring-1 ring-white/5 overflow-visible">
        {slots.map(slot => {
          const q         = slot.quality as keyof typeof QUALITY_TRACK
          const startPct  = ((dayjs(slot.startTime).valueOf() - minMs) / span) * 100
          const widthPct  = ((dayjs(slot.endTime).valueOf() - dayjs(slot.startTime).valueOf()) / span) * 100
          return (
            <div
              key={slot.id}
              title={`${dayjs(slot.startTime).format('HH:mm')}–${dayjs(slot.endTime).format('HH:mm')} · ${slot.category}`}
              className={cn(
                'absolute top-1 bottom-1 min-w-[4px] rounded-md transition-colors cursor-default',
                QUALITY_TRACK[q] ?? QUALITY_TRACK.neutral,
              )}
              style={{ left: `${startPct}%`, width: `${widthPct}%` }}
            />
          )
        })}

        <CurrentTimeIndicator minMs={minMs} maxMs={maxMs} />
      </div>

      {/* Time axis labels */}
      <div className="flex justify-between px-0.5">
        <span className="text-star-dim text-2xs font-mono tabular-nums">{dayjs(minMs).format('HH:mm')}</span>
        <span className="text-star-dim text-2xs font-mono tabular-nums">{dayjs(maxMs).format('HH:mm')}</span>
      </div>

      {/* ── Slot list ── */}
      <div className="space-y-1.5">
        {slots.slice(0, 5).map(slot => {
          const q     = slot.quality as keyof typeof QUALITY_TRACK
          const start = dayjs(slot.startTime).format('HH:mm')
          const end   = dayjs(slot.endTime).format('HH:mm')
          return (
            <div
              key={slot.id}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg',
                QUALITY_TRACK[q] ?? QUALITY_TRACK.neutral,
              )}
            >
              <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', QUALITY_DOT[q] ?? 'bg-star-muted')} />
              <span className="text-star-soft text-xs font-mono tabular-nums">{start}–{end}</span>
              <span className="text-star-dim text-2xs flex-1 truncate">{slot.category}</span>
              {slot.reasons?.[0] && (
                <span className="text-star-dim text-2xs hidden sm:block truncate max-w-[120px]">{slot.reasons[0]}</span>
              )}
              <div className="w-12 h-1 rounded-full bg-cosmic-mist overflow-hidden flex-shrink-0">
                <div
                  className={cn('h-full rounded-full', QUALITY_SCORE_BAR[q] ?? 'bg-star-dim')}
                  style={{ width: `${slot.score}%` }}
                />
              </div>
              <span className="text-star-dim text-2xs font-mono tabular-nums w-5 text-right">{slot.score}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
